/**
 * Phase 8 · Smart Match prompt → Pinecone search (24-Apr follow-up).
 *
 * Closes the gap between the intake prompt and the match canvas: when the
 * user submits a JD / free-form brief on `/smart-match/new`, the canvas
 * used to just render whatever rows happened to live in
 * `ggj_b2b_match_results` for the org (usually empty) and the three
 * engines (inference / neural re-ranker / recommendation) were never
 * invoked.
 *
 * This module wires the first two engines — semantic retrieval via
 * Pinecone (`ggj_b2b_pinecone`) and signal fusion via
 * `runInference` — directly to the submit path so the user sees real
 * candidates within ~1-2 seconds.
 *
 * Pipeline:
 *
 *   1. embed(prompt)                       → 384-dim vector (edge fn)
 *   2. pinecone.query(ggj-resumes, topK)   → [{id, score}]
 *   3. supabase.select(candidates)         → enrich ids with rows
 *   4. runInference per candidate          → composite score
 *   5. return MatchResult[]                → rendered in the canvas
 *
 * The Pinecone call uses the org's resolved namespace
 * (`orgMatchingConfigService.resolvePineconeNamespace`) so results are
 * multi-tenant-isolated per `docs/smart-match/01_ARCHITECTURE.md §13`.
 *
 * No DB persistence — results are session-scoped to this pipelineId.
 * Persistence (upsert to `ggj_b2b_match_results`) happens from the
 * existing `scoreAndPersist` / `scoreJobAgainstPool` paths when the
 * user actually saves a JD; keeping search results ephemeral avoids
 * polluting the shared KPI table with one-off exploratory queries.
 */

import type { Candidate, Job } from '@core/types';
import { supabase } from '@core/supabase/client';
import { orgMatchingConfigService } from '@core/supabase/services/orgMatchingConfigService';
import { skillTaxonomyService } from '@core/supabase/services/skillTaxonomyService';
import { matchResultService } from '@core/supabase/services/matchResultService';

/**
 * Build the alias → canonical synonym map expected by
 * `computeSkillOverlap` from the taxonomy rows. Pulled inline here
 * because `skillTaxonomyService` exposes `getWithAliases` but not a
 * pre-built map.
 */
async function loadOrgSkillContext(
  orgId: string,
): Promise<{ canonicalNames: string[]; synonymMap: Map<string, string> }> {
  try {
    const rows = await skillTaxonomyService.getWithAliases(orgId);
    const synonymMap = new Map<string, string>();
    const canonicalNames: string[] = [];
    for (const row of rows) {
      const canonical = row.canonical_name.trim();
      if (!canonical) continue;
      canonicalNames.push(canonical);
      const canonicalLower = canonical.toLowerCase();
      synonymMap.set(canonicalLower, canonical);
      for (const alias of row.aliases ?? []) {
        const a = alias.trim().toLowerCase();
        if (a) synonymMap.set(a, canonical);
      }
    }
    return { canonicalNames, synonymMap };
  } catch (err) {
    console.warn(
      '[promptSearch] taxonomy load failed:',
      err instanceof Error ? err.message : err,
    );
    return { canonicalNames: [], synonymMap: new Map() };
  }
}
import type { MatchResult, InferenceResult } from '../types';
import { runInference } from '../engine/scoring/inferenceEngine';
import { normalizeCosineSimilarity } from '../engine/scoring/semanticScore';
import { computeSkillOverlap } from '../engine/scoring/skillOverlap';
import {
  experienceScore,
  budgetScore,
  locationScore,
  deadlineScore,
} from '../engine/scoring/dimensionScores';

const DEFAULT_TOP_K = 25;
/**
 * Hard cap on the per-JD text length we send to the embed edge
 * function. Groq's embedding endpoint silently truncates, but we hit
 * 413s on very large JDs (50k+ chars) before that happens, so we
 * truncate client-side with a small head + tail so skills-at-the-
 * bottom JDs don't lose their "Must have" section.
 */
const MAX_JD_CHARS_FOR_EMBED = 12000;

export interface PromptSearchOptions {
  /** Candidates to request from Pinecone. Defaults to 25. */
  topK?: number;
  /** Optional per-call timeout (ms). Defaults to 20000. */
  timeoutMs?: number;
}

export interface PromptSearchResult {
  /** Ephemeral MatchResult rows, sorted by composite_score desc. */
  matches: MatchResult[];
  /** Source of the result set — useful for surfacing "no index yet" banners. */
  source: 'pinecone' | 'fallback-pool' | 'empty';
  /**
   * Lightweight debug info: candidate counts at each pipeline stage.
   * Surfaced in dev to diagnose "why did I get 0 matches".
   */
  debug: {
    promptLength: number;
    pineconeMatchCount: number;
    hydratedCandidateCount: number;
    scoredCount: number;
  };
}

// ── Helpers ────────────────────────────────────────────────────────────────

/**
 * Lightweight Pinecone circuit breaker — mirrors the Llama pattern in
 * `src/core/ai/llamaChat.ts`. When the edge function (`ggj_b2b_pinecone`)
 * returns consecutive 502 / non-2xx responses we stop calling it for a
 * cooldown window and let `fallbackPoolSearch` / per-JD fallback carry
 * the canvas. This avoids three visible side-effects users hit today:
 *
 *   1. Browser dev-tools logging a fresh 502 for every keystroke-driven
 *      React Query refetch even though our code already handles it.
 *   2. Supabase edge-function quota burn on a known-down backend.
 *   3. UI latency spikes (each 502 costs ~2-4 s round-trip).
 *
 * The breaker is module-local — one per tab session. We persist the
 * open-until timestamp in `sessionStorage` so a hard refresh during
 * an outage doesn't immediately retry (the Pinecone / Groq-side
 * outages usually last 20+ minutes, same order of magnitude as the
 * Llama rate-limit response we already handle).
 */
const PINECONE_BREAKER_KEY = 'ggj.pinecone.breakerOpenUntil';
const PINECONE_BREAKER_FAILURE_THRESHOLD = 2;
const PINECONE_BREAKER_COOLDOWN_MS = 5 * 60_000; // 5 min — matches ggj_b2b_pinecone SLO

let pineconeFailureCount = 0;
let pineconeBreakerNotified = false;

function readBreakerOpenUntil(): number {
  try {
    const raw =
      typeof window !== 'undefined'
        ? window.sessionStorage.getItem(PINECONE_BREAKER_KEY)
        : null;
    const n = raw == null ? 0 : Number(raw);
    return Number.isFinite(n) ? n : 0;
  } catch {
    return 0;
  }
}

function writeBreakerOpenUntil(ts: number): void {
  try {
    if (typeof window === 'undefined') return;
    if (ts > Date.now()) {
      window.sessionStorage.setItem(PINECONE_BREAKER_KEY, String(ts));
    } else {
      window.sessionStorage.removeItem(PINECONE_BREAKER_KEY);
    }
  } catch {
    /* ignore quota / private mode */
  }
}

function pineconeBreakerIsOpen(): boolean {
  return Date.now() < readBreakerOpenUntil();
}

function notePineconeFailure(): void {
  pineconeFailureCount += 1;
  if (pineconeFailureCount >= PINECONE_BREAKER_FAILURE_THRESHOLD) {
    const until = Date.now() + PINECONE_BREAKER_COOLDOWN_MS;
    writeBreakerOpenUntil(until);
    if (!pineconeBreakerNotified) {
      console.warn(
        `[promptSearch] Pinecone edge function failing — pausing semantic search for ${Math.round(
          PINECONE_BREAKER_COOLDOWN_MS / 1000,
        )}s. Falling back to pool scoring.`,
      );
      pineconeBreakerNotified = true;
    }
  }
}

function notePineconeSuccess(): void {
  pineconeFailureCount = 0;
  if (pineconeBreakerNotified) {
    console.info('[promptSearch] Pinecone recovered — resuming semantic search.');
  }
  pineconeBreakerNotified = false;
  writeBreakerOpenUntil(0);
}

/**
 * Public getter so the canvas can render a "semantic search paused"
 * banner instead of the generic "vector index offline" copy.
 */
export function isPineconeBreakerOpen(): boolean {
  return pineconeBreakerIsOpen();
}

async function callPinecone(route: string, body: Record<string, unknown>): Promise<unknown> {
  // Short-circuit while the breaker is open so the caller falls
  // straight through to the pool fallback without a 2-4 s round-trip
  // + red dev-tools entry for each request.
  if (pineconeBreakerIsOpen()) {
    throw new Error('pinecone-breaker-open');
  }

  const { data, error } = await supabase.functions.invoke('ggj_b2b_pinecone', {
    body: { route, ...body },
  });
  if (error) {
    notePineconeFailure();
    throw new Error(`Pinecone ${route} failed: ${error.message}`);
  }
  if (
    data != null &&
    typeof data === 'object' &&
    'error' in data &&
    (data as { error: unknown }).error != null
  ) {
    notePineconeFailure();
    throw new Error(String((data as { error: unknown }).error));
  }
  notePineconeSuccess();
  return data;
}

async function embedPrompt(prompt: string): Promise<number[] | null> {
  try {
    const out = (await callPinecone('embed', { text: prompt })) as
      | { vector?: number[] }
      | null;
    const vec = out?.vector;
    return Array.isArray(vec) && vec.length > 0 ? vec : null;
  } catch (err) {
    // Swallow breaker-open marker silently — the user has already
    // been told once; log everything else so real regressions
    // surface in the logs.
    const msg = err instanceof Error ? err.message : String(err);
    if (msg !== 'pinecone-breaker-open') {
      console.warn('[promptSearch] embed failed:', msg);
    }
    return null;
  }
}

interface PineconeHit {
  id: string;
  score: number;
  metadata?: Record<string, unknown>;
}

async function queryResumes(
  vector: number[],
  orgId: string,
  topK: number,
): Promise<PineconeHit[]> {
  try {
    const namespace = await orgMatchingConfigService
      .getForOrg(orgId)
      .then((cfg) => orgMatchingConfigService.resolvePineconeNamespace(cfg))
      .catch(() => `org-${orgId}`);

    const out = (await callPinecone('query', {
      index: 'ggj-resumes',
      vector,
      topK,
      namespace,
      filter: { org_id: { $eq: orgId } },
    })) as { matches?: PineconeHit[] } | null;

    return Array.isArray(out?.matches) ? out!.matches : [];
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    if (msg !== 'pinecone-breaker-open') {
      console.warn('[promptSearch] pinecone query failed:', msg);
    }
    return [];
  }
}

async function hydrateCandidates(
  orgId: string,
  ids: string[],
): Promise<Map<string, Candidate>> {
  if (ids.length === 0) return new Map();
  const { data, error } = await supabase
    .from('ggj_b2b_candidates')
    .select('*')
    .eq('org_id', orgId)
    .eq('is_active', true)
    .in('id', ids);
  if (error) throw error;
  const out = new Map<string, Candidate>();
  for (const row of (data ?? []) as Candidate[]) out.set(row.id, row);
  return out;
}

/**
 * Lightweight keyword pick from the prompt. We can't run the full LLM
 * JD extractor on every keystroke, so we approximate `skills_required`
 * by intersecting prompt tokens with the org's skill taxonomy. This
 * lets `computeSkillOverlap` do real work instead of bailing to its
 * legacy fallback.
 */
function extractSkillsFromPrompt(prompt: string, taxonomy: string[]): string[] {
  const lowered = prompt.toLowerCase();
  const out: string[] = [];
  for (const skill of taxonomy) {
    const s = skill.trim();
    if (s.length === 0) continue;
    // Word-boundary match so "go" doesn't match "google".
    const pattern = new RegExp(`(^|[^a-z0-9+#])${escapeRegex(s.toLowerCase())}([^a-z0-9+#]|$)`);
    if (pattern.test(lowered)) out.push(s);
  }
  return Array.from(new Set(out));
}

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// ── Text-relevance scoring for fallback (no Pinecone vectors) ───────────────

const STOP_WORDS = new Set([
  'the', 'and', 'for', 'are', 'you', 'with', 'this', 'that', 'have',
  'will', 'from', 'they', 'been', 'has', 'their', 'not', 'but', 'all',
  'when', 'your', 'can', 'who', 'its', 'our', 'more', 'also', 'which',
  'into', 'should', 'must', 'what', 'any', 'would', 'some', 'than',
  'about', 'experience', 'working', 'work', 'role', 'team', 'skills',
  'ability', 'strong', 'excellent', 'good', 'great', 'able', 'looking',
  'seeking', 'required', 'preferred', 'plus', 'bonus', 'nice', 'years',
  'minimum', 'minimum', 'key', 'responsibilities', 'requirements', 'position',
]);

function tokenizeText(text: string): Set<string> {
  if (!text) return new Set();
  return new Set(
    text
      .toLowerCase()
      .split(/[\s,;|\/\-–—()\[\]+.:\n\r'"!?@#$%^&*=_~`<>{}\\]+/)
      .filter((w) => w.length >= 3 && !STOP_WORDS.has(w)),
  );
}

/**
 * Candidate-to-JD text relevance: 0–100.
 *
 * Replaces the neutral `semantic=50` sentinel in the fallback pool path so
 * candidates whose `parsed_skills`, `current_title`, and `current_company`
 * actually overlap with the JD text surface higher than completely
 * unrelated ones. Maps to [35, 88] so it never dominates the composite
 * when signals are sparse.
 *
 * This is a cheap keyword-intersection approach — accurate enough for the
 * fallback path where Pinecone is unavailable. Real semantic similarity
 * kicks in once vectors are indexed.
 */
export function computeTextRelevance(candidate: Candidate, jdText: string): number {
  if (!jdText.trim()) return 50;
  const jdWords = tokenizeText(jdText);
  if (jdWords.size === 0) return 50;

  const candidateText = [
    ...(candidate.parsed_skills ?? []),
    candidate.current_title ?? '',
    candidate.current_company ?? '',
    // notes and tags also carry useful signal
    candidate.notes ?? '',
    ...(candidate.tags ?? []),
  ].join(' ');

  const candidateWords = tokenizeText(candidateText);
  if (candidateWords.size === 0) return 40;

  // How much of the JD's vocabulary does this candidate cover?
  let hits = 0;
  for (const w of candidateWords) {
    if (jdWords.has(w)) hits += 1;
  }
  const coverage = hits / jdWords.size; // 0 … 1 (can exceed 1: clamped below)
  return Math.round(35 + Math.min(coverage, 1) * 53);
}

/**
 * Build a synthetic `Job` object from the prompt so the scoring layer can
 * reuse `computeSkillOverlap` / `experienceScore` etc. without creating
 * a real pipeline row in the jobs table.
 */
function syntheticJobFromPrompt(
  orgId: string,
  prompt: string,
  skills: string[],
  overrides: {
    id?: string;
    title?: string;
    clientId?: string | null;
    clientName?: string | null;
  } = {},
): Job {
  const title =
    overrides.title ??
    inferTitleFromKeywords(prompt, skills) ??
    prompt.slice(0, 80) ??
    'Untitled role';
  return {
    id: overrides.id ?? '__prompt__',
    org_id: orgId,
    client_id: overrides.clientId ?? null,
    title,
    description: prompt,
    skills_required: skills,
    skills_nice_to_have: [],
    experience_min: null,
    experience_max: null,
    budget_min: null,
    budget_max: null,
    budget_currency: 'USD',
    hiring_deadline: null,
    location: null,
    remote_policy: null,
    employment_type: null,
    status: 'open',
    created_by: '',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    // Attach a lightweight client stub so grouping-by-client picks up
    // a human label even when there's no real client row yet.
    client: overrides.clientName
      ? { id: overrides.clientId ?? null, name: overrides.clientName }
      : null,
  } as unknown as Job;
}

/**
 * Best-effort JD title inference from the raw prompt + the skills the
 * taxonomy already flagged. Used when the user uploads a JD without a
 * clear title line (image 3 "auto-generate JD name from keywords"
 * ask). Runs in three waves:
 *   1. First H1 / role-style line from the top of the JD body.
 *   2. "<Seniority> <Role>" recognised from the first 400 chars.
 *   3. Fallback: "<Top skill> role" (e.g. "Python role") if skills exist.
 */
export function inferTitleFromKeywords(
  prompt: string,
  skills: string[],
): string | null {
  const trimmed = prompt.trim();
  if (!trimmed) return null;

  // 1. Pick the first non-empty line that looks like a title — short
  //    (<= 80 chars), mostly letters, no trailing period.
  const firstLine = trimmed
    .split(/\r?\n/)
    .map((l) => l.trim())
    .find((l) => l.length > 0 && l.length <= 80 && !/[.?!]$/.test(l));
  if (firstLine && /^[A-Z][A-Za-z0-9 /&,\-()]+$/.test(firstLine)) {
    return firstLine;
  }

  // 2. Look for "(Senior|Lead|Staff|Principal|Junior) <Word>".
  const head = trimmed.slice(0, 400).replace(/\s+/g, ' ');
  const senRe =
    /\b(Junior|Mid|Senior|Staff|Principal|Lead|Head of)\s+([A-Z][A-Za-z]+(?:\s+[A-Z][A-Za-z]+){0,3})/;
  const senMatch = head.match(senRe);
  if (senMatch) return `${senMatch[1]} ${senMatch[2]}`.slice(0, 80);

  // 3. Fallback: top skill as the role anchor.
  if (skills.length > 0) {
    const pick = skills[0]!;
    return `${pick} role`;
  }
  return null;
}

/**
 * Auto-generate a client / brand fallback name when a JD doesn't carry
 * one. Reuses the title stem so the group header reads sensibly (e.g.
 * "Untagged · Senior Python Engineer").
 */
export function defaultClientNameForJd(title: string): string {
  const stem = title.trim().split(/[\s·|–-]+/).filter(Boolean).slice(0, 3).join(' ');
  return `Untagged · ${stem || 'JD'}`;
}

/** Build a MatchResult from an inference result + a candidate. */
function toMatchResult(
  orgId: string,
  candidate: Candidate,
  inference: InferenceResult,
  job?: Job,
): MatchResult {
  const now = new Date().toISOString();
  const jobId = job?.id ?? '__prompt__';
  return {
    id: `${jobId}:${candidate.id}`,
    org_id: orgId,
    candidate_id: candidate.id,
    job_id: jobId,
    composite_score: inference.compositeScore,
    dimension_scores: inference.dimensionScores,
    matched_skills: inference.matched,
    missing_skills: inference.missing,
    bonus_skills: inference.bonus,
    recommendation: inference.recommendation,
    analysis_json: { band: inference.band, source: 'prompt-search' },
    match_version: 1,
    matched_at: now,
    created_at: now,
    updated_at: now,
    candidate,
    ...(job ? { job } : {}),
  } as MatchResult;
}

// ── Cross-score: DB candidates × real org jobs ──────────────────────────────

/**
 * Fire-and-forget: score `candidates` against the org's active real jobs
 * and write every (candidate × job) pair to `ggj_b2b_match_results`.
 *
 * WHY — In single-JD prompt-search mode, every MatchResult has the same
 * synthetic `job_id = '__prompt__'`.  The `useAlternateJobsForCandidates`
 * hook then queries `ggj_b2b_match_results` for *other* jobs those
 * candidates fit, but finds nothing (ephemeral search results are never
 * persisted).  The "Also a fit for" card footer is therefore always blank.
 *
 * By scoring the returned candidates against the org's real active jobs
 * and persisting those rows, we give the footer real data: next time the
 * hook fetches (after `useSmartMatchSearch` invalidates the alt-jobs key),
 * every candidate card can answer "what other roles in our system is this
 * person a good fit for?"
 *
 * Capped at 6 jobs × 15 candidates = 90 pairs — pure client-side scoring,
 * zero extra Pinecone calls.  Non-blocking: called with `void`, failures
 * are swallowed so they never surface as UI errors.
 */
export async function crossScoreOrgCandidatesVsJobs(
  orgId: string,
  candidates: Candidate[],
  synonymMap: Map<string, string>,
): Promise<void> {
  if (!orgId || candidates.length === 0) return;
  try {
    const { data: jobRows, error } = await supabase
      .from('ggj_b2b_jobs')
      .select(
        'id, org_id, client_id, title, description, skills_required, skills_nice_to_have, experience_min, experience_max, budget_min, budget_max, budget_currency, hiring_deadline, location, remote_policy, employment_type, status, created_at, updated_at, client:ggj_b2b_clients(id, name)',
      )
      .eq('org_id', orgId)
      .in('status', ['open', 'on_hold'])
      .order('created_at', { ascending: false })
      .limit(6);
    if (error || !jobRows || jobRows.length === 0) return;

    type JobRow = Job & { client: { id: string; name: string } | null };
    const jobs = jobRows as unknown as JobRow[];

    const persistRows: Parameters<typeof matchResultService.upsert>[1][] = [];
    for (const candidate of candidates.slice(0, 15)) {
      for (const job of jobs) {
        const skillResult = computeSkillOverlap(candidate, job as Job, synonymMap);
        // Use text-relevance as the semantic proxy — no extra Pinecone call.
        const semantic = computeTextRelevance(
          candidate,
          (job as { description?: string | null }).description ?? job.title ?? '',
        );
        const exp = experienceScore(
          candidate.total_experience_years ?? candidate.years_exp,
          (job as { experience_min?: number | null }).experience_min ?? null,
          (job as { experience_max?: number | null }).experience_max ?? null,
        );
        const budget = budgetScore(
          candidate.expected_ctc,
          (job as { budget_min?: number | null }).budget_min ?? null,
          (job as { budget_max?: number | null }).budget_max ?? null,
        );
        const loc = locationScore(
          candidate.location,
          (candidate as { preferred_locations?: string[] | null }).preferred_locations ?? null,
          (job as { location?: string | null }).location ?? null,
          (job as { remote_policy?: string | null }).remote_policy ?? null,
        );
        const deadline = deadlineScore(
          (candidate as { notice_period_days?: number | null }).notice_period_days ?? null,
          (job as { hiring_deadline?: string | null }).hiring_deadline ?? null,
        );
        const inference = runInference(skillResult, semantic, exp, budget, loc, deadline);
        persistRows.push({
          candidate_id: candidate.id,
          job_id: job.id,
          composite_score: inference.compositeScore,
          dimension_scores: inference.dimensionScores,
          matched_skills: inference.matched,
          missing_skills: inference.missing,
          bonus_skills: inference.bonus,
          recommendation: inference.recommendation,
        });
      }
    }
    if (persistRows.length > 0) {
      await matchResultService.upsertBatch(orgId, persistRows);
    }
  } catch (err) {
    console.warn(
      '[crossScoreOrgCandidatesVsJobs] non-critical failure:',
      err instanceof Error ? err.message : err,
    );
  }
}

// ── Public API ─────────────────────────────────────────────────────────────

/**
 * Run a Pinecone-backed semantic search for the given prompt and score
 * the resulting candidates through the inference engine.
 *
 * Returns `MatchResult[]` sorted by composite score descending. Never
 * throws — any upstream failure (edge function down, empty index,
 * network) is logged and surfaced via the `source` / `debug` fields.
 */
export async function runPromptSearch(
  orgId: string,
  prompt: string,
  opts: PromptSearchOptions = {},
): Promise<PromptSearchResult> {
  const topK = opts.topK ?? DEFAULT_TOP_K;
  const debug = {
    promptLength: prompt.length,
    pineconeMatchCount: 0,
    hydratedCandidateCount: 0,
    scoredCount: 0,
  };

  if (!orgId || prompt.trim().length < 3) {
    return { matches: [], source: 'empty', debug };
  }

  // 1. Embed
  const vector = await embedPrompt(prompt);
  if (!vector) {
    // Embed edge function unavailable (502, rate-limit, cold start, …).
    // Instead of returning empty — which was the #1 reason the canvas
    // appeared "unscored" even for valid prompts — fall through to the
    // pool search so the inference engine still produces deterministic
    // rows from metadata. Source stays `fallback-pool` so the canvas
    // can surface the "vector index offline" banner.
    return fallbackPoolSearch(orgId, prompt, topK, debug);
  }

  // 2. Pinecone query (ggj-resumes)
  const hits = await queryResumes(vector, orgId, topK);
  debug.pineconeMatchCount = hits.length;

  // 3. Hydrate candidates
  const hitIds = hits.map((h) => h.id);
  let candidateMap: Map<string, Candidate>;
  try {
    candidateMap = await hydrateCandidates(orgId, hitIds);
  } catch (err) {
    console.warn(
      '[promptSearch] candidate hydration failed:',
      err instanceof Error ? err.message : err,
    );
    return fallbackPoolSearch(orgId, prompt, topK, debug);
  }
  debug.hydratedCandidateCount = candidateMap.size;

  if (candidateMap.size === 0) {
    // Fallback: Pinecone has no vectors for this org yet. Pull the
    // first N candidates from the pool so the user still sees data
    // while the vector index is populated.
    return fallbackPoolSearch(orgId, prompt, topK, debug);
  }

  // 4. Extract skills from prompt using org taxonomy
  const { canonicalNames: taxonomyNames, synonymMap } = await loadOrgSkillContext(orgId);
  const skillsFromPrompt = extractSkillsFromPrompt(prompt, taxonomyNames);
  const syntheticJob = syntheticJobFromPrompt(orgId, prompt, skillsFromPrompt);

  // 5. Run inference per candidate
  const matches: MatchResult[] = [];
  for (const hit of hits) {
    const candidate = candidateMap.get(hit.id);
    if (!candidate) continue;

    const skillResult = computeSkillOverlap(candidate, syntheticJob, synonymMap);
    const semantic = normalizeCosineSimilarity(hit.score);
    const exp = experienceScore(
      candidate.total_experience_years ?? candidate.years_exp,
      null,
      null,
    );
    const budget = budgetScore(candidate.expected_ctc, null, null);
    const loc = locationScore(
      candidate.location,
      (candidate as { preferred_locations?: string[] | null }).preferred_locations ?? null,
      null,
      null,
    );
    const deadline = deadlineScore(
      (candidate as { notice_period_days?: number | null }).notice_period_days ?? null,
      null,
    );

    const inference = runInference(skillResult, semantic, exp, budget, loc, deadline);
    matches.push(toMatchResult(orgId, candidate, inference, syntheticJob));
  }

  matches.sort((a, b) => b.composite_score - a.composite_score);
  debug.scoredCount = matches.length;

  // Background: score these candidates against real org jobs so the
  // "Also a fit for" footer is populated on next render.
  void crossScoreOrgCandidatesVsJobs(
    orgId,
    Array.from(candidateMap.values()).slice(0, 15),
    synonymMap,
  );

  return {
    matches,
    source: matches.length > 0 ? 'pinecone' : 'empty',
    debug,
  };
}

/**
 * When Pinecone returns no hits (fresh org, vectors not yet indexed),
 * fall back to pulling the first N candidates from the pool and
 * scoring them by whatever metadata we have. Ensures the canvas
 * always shows *something* the user can triage while the indexing
 * backfill runs.
 */
async function fallbackPoolSearch(
  orgId: string,
  prompt: string,
  topK: number,
  debug: PromptSearchResult['debug'],
): Promise<PromptSearchResult> {
  const { data, error } = await supabase
    .from('ggj_b2b_candidates')
    .select('*')
    .eq('org_id', orgId)
    .eq('is_active', true)
    .order('created_at', { ascending: false })
    .limit(topK);
  if (error) {
    console.warn('[promptSearch] fallback list failed:', error.message);
    return { matches: [], source: 'empty', debug };
  }
  const candidates = (data ?? []) as Candidate[];
  debug.hydratedCandidateCount = candidates.length;
  if (candidates.length === 0) {
    return { matches: [], source: 'empty', debug };
  }

  const { canonicalNames: taxonomyNames, synonymMap } = await loadOrgSkillContext(orgId);
  const skillsFromPrompt = extractSkillsFromPrompt(prompt, taxonomyNames);
  const syntheticJob = syntheticJobFromPrompt(orgId, prompt, skillsFromPrompt);

  const matches: MatchResult[] = [];
  for (const candidate of candidates) {
    const skillResult = computeSkillOverlap(candidate, syntheticJob, synonymMap);
    // Use text-relevance instead of neutral 50 so candidates whose skills /
    // title / company actually match the JD surface higher than unrelated ones.
    const semantic = computeTextRelevance(candidate, prompt);
    const exp = experienceScore(
      candidate.total_experience_years ?? candidate.years_exp,
      null,
      null,
    );
    const budget = budgetScore(candidate.expected_ctc, null, null);
    const loc = locationScore(
      candidate.location,
      (candidate as { preferred_locations?: string[] | null }).preferred_locations ?? null,
      null,
      null,
    );
    const deadline = deadlineScore(
      (candidate as { notice_period_days?: number | null }).notice_period_days ?? null,
      null,
    );
    const inference = runInference(skillResult, semantic, exp, budget, loc, deadline);
    matches.push(toMatchResult(orgId, candidate, inference, syntheticJob));
  }

  matches.sort((a, b) => b.composite_score - a.composite_score);
  debug.scoredCount = matches.length;

  // Background: cross-score vs real org jobs for "Also a fit for".
  void crossScoreOrgCandidatesVsJobs(orgId, candidates.slice(0, 15), synonymMap);

  return {
    matches,
    source: matches.length > 0 ? 'fallback-pool' : 'empty',
    debug,
  };
}

// ── N×M multi-JD search (24-Apr docs §12 dataflow) ─────────────────────────

/**
 * A single JD parcel — whatever the user pasted/uploaded in the
 * MultiJdList. `id` is the entry id on the thread; `clientName` is
 * optional (auto-generated when absent).
 */
export interface MultiJdInput {
  id: string;
  /** May be a filename, user-entered title, or empty. */
  title?: string | null;
  text: string;
  clientId?: string | null;
  clientName?: string | null;
}

export interface MultiJdSearchOptions extends PromptSearchOptions {
  /** Max candidates to pull per JD (default 25). */
  perJdTopK?: number;
  /** Max JDs processed in one run (default 10 — matches intake cap). */
  maxJds?: number;
}

export interface MultiJdSearchResult {
  /**
   * Flat `MatchResult[]` over every (JD × candidate) pair that scored.
   * Tagged with `job_id` + `job.title` + `job.client.name` so the
   * canvas can group by client / JD without another lookup.
   */
  matches: MatchResult[];
  /** Per-JD metadata — useful for the group header, KPI cards, reports. */
  jds: Array<{
    id: string;
    title: string;
    clientName: string;
    scoredCount: number;
    strongCount: number;
  }>;
  source: 'pinecone' | 'fallback-pool' | 'mixed' | 'empty';
  debug: {
    jdCount: number;
    totalScored: number;
    skippedJds: number;
  };
}

/**
 * The N×M smart-match core — for every JD in `jds`, embed → Pinecone
 * top-K → inference, then merge the flat result set. Missing titles
 * are auto-generated from keywords; missing client names fall back to
 * "Untagged · <title>". Each row is tagged with `job_id` / `job` so
 * the canvas can group / filter / export by JD or client.
 *
 * Failures on individual JDs never abort the run — the system keeps
 * scoring the others and reports skipped JDs in `debug.skippedJds`.
 */
export async function runMultiJdSearch(
  orgId: string,
  jds: MultiJdInput[],
  opts: MultiJdSearchOptions = {},
): Promise<MultiJdSearchResult> {
  const maxJds = opts.maxJds ?? 10;
  const perJdTopK = opts.perJdTopK ?? 25;
  const limited = jds.slice(0, maxJds);

  const debug = { jdCount: limited.length, totalScored: 0, skippedJds: 0 };
  if (!orgId || limited.length === 0) {
    return { matches: [], jds: [], source: 'empty', debug };
  }

  const { canonicalNames: taxonomyNames, synonymMap } = await loadOrgSkillContext(orgId);
  const jdMeta: MultiJdSearchResult['jds'] = [];
  const allMatches: MatchResult[] = [];
  const sources = new Set<'pinecone' | 'fallback-pool'>();

  for (const jd of limited) {
    if (!jd.text || jd.text.trim().length < 3) {
      debug.skippedJds += 1;
      continue;
    }

    const skillsFromJd = extractSkillsFromPrompt(jd.text, taxonomyNames);
    // Treat the intake placeholder pattern ("JD 1", "JD 2", …) the
    // same as a missing title — we want a meaningful keyword-derived
    // title on the match card, group header, and exports. Anything
    // the user actually typed / a filename-derived title is kept.
    const rawTitle = jd.title?.trim() ?? '';
    const isPlaceholderTitle = /^JD\s+\d+$/i.test(rawTitle);
    const userTitle = rawTitle && !isPlaceholderTitle ? rawTitle : null;
    const resolvedTitle =
      userTitle ??
      inferTitleFromKeywords(jd.text, skillsFromJd) ??
      rawTitle ??
      `Untitled JD`;
    const resolvedClient =
      jd.clientName && jd.clientName.trim().length > 0
        ? jd.clientName.trim()
        : defaultClientNameForJd(resolvedTitle);
    const syntheticJob = syntheticJobFromPrompt(orgId, jd.text, skillsFromJd, {
      id: jd.id,
      title: resolvedTitle,
      clientId: jd.clientId ?? null,
      clientName: resolvedClient,
    });

    // Embed → query → hydrate. If embed/query fail we fall back to the
    // pool so each JD still produces *some* scored rows. We truncate
    // very large JDs with a head+tail window so the embed call stays
    // under the edge function's 413 threshold without losing the
    // "Must have / Nice to have" section that usually sits at the end.
    const embedText =
      jd.text.length <= MAX_JD_CHARS_FOR_EMBED
        ? jd.text
        : `${jd.text.slice(0, MAX_JD_CHARS_FOR_EMBED - 2000)}\n…\n${jd.text.slice(-2000)}`;
    const vector = await embedPrompt(embedText);
    let hits: PineconeHit[] = [];
    if (vector) {
      hits = await queryResumes(vector, orgId, perJdTopK);
    }
    let candidates: Candidate[] = [];
    let source: 'pinecone' | 'fallback-pool' = 'pinecone';
    if (hits.length > 0) {
      try {
        const hydrated = await hydrateCandidates(
          orgId,
          hits.map((h) => h.id),
        );
        if (hydrated.size === 0) {
          throw new Error('no hydrated candidates');
        }
        candidates = hits
          .map((h) => hydrated.get(h.id))
          .filter((c): c is Candidate => Boolean(c));
      } catch {
        source = 'fallback-pool';
      }
    } else {
      source = 'fallback-pool';
    }

    if (source === 'fallback-pool') {
      const { data, error } = await supabase
        .from('ggj_b2b_candidates')
        .select('*')
        .eq('org_id', orgId)
        .eq('is_active', true)
        .order('created_at', { ascending: false })
        .limit(perJdTopK);
      if (error) {
        debug.skippedJds += 1;
        continue;
      }
      candidates = (data ?? []) as Candidate[];
      hits = candidates.map((c) => ({ id: c.id, score: 0.5 }));
    }
    sources.add(source);

    let strongCount = 0;
    for (let i = 0; i < candidates.length; i++) {
      const candidate = candidates[i]!;
      const hit = hits[i];
      const skillResult = computeSkillOverlap(candidate, syntheticJob, synonymMap);
      const semantic =
        source === 'pinecone' && hit
          ? normalizeCosineSimilarity(hit.score)
          : computeTextRelevance(candidate, jd.text);
      const exp = experienceScore(
        candidate.total_experience_years ?? candidate.years_exp,
        null,
        null,
      );
      const budget = budgetScore(candidate.expected_ctc, null, null);
      const loc = locationScore(
        candidate.location,
        (candidate as { preferred_locations?: string[] | null })
          .preferred_locations ?? null,
        null,
        null,
      );
      const deadline = deadlineScore(
        (candidate as { notice_period_days?: number | null })
          .notice_period_days ?? null,
        null,
      );
      const inference = runInference(
        skillResult,
        semantic,
        exp,
        budget,
        loc,
        deadline,
      );
      if (inference.band === 'strong_fit') strongCount += 1;
      allMatches.push(toMatchResult(orgId, candidate, inference, syntheticJob));
    }

    jdMeta.push({
      id: jd.id,
      title: resolvedTitle,
      clientName: resolvedClient,
      scoredCount: candidates.length,
      strongCount,
    });
  }

  allMatches.sort((a, b) => b.composite_score - a.composite_score);
  debug.totalScored = allMatches.length;

  const srcVariant: MultiJdSearchResult['source'] =
    allMatches.length === 0
      ? 'empty'
      : sources.size === 2
        ? 'mixed'
        : sources.has('pinecone')
          ? 'pinecone'
          : 'fallback-pool';

  return {
    matches: allMatches,
    jds: jdMeta,
    source: srcVariant,
    debug,
  };
}

// ── Bulk-resume search (21-Apr-26) ─────────────────────────────────────────
//
// When the user uploads resumes via §29.F Bulk mode, the scoring path is
// inverted from the standard prompt → Pinecone → candidates flow: the
// candidates *are* the uploaded files (not in the DB, not in Pinecone),
// and we score each of them against either a context JD or a synthesised
// JD derived from the file names / paraphrase.
//
// User-reported bug (21-Apr-26): "on upload of resume i dont see the
// matching". Root cause: the old flow stored only resume metadata and
// fired `runPromptSearch(paraphrase)` — which embedded a junk summary
// string like "Bulk resumes (3): CV_foo.pdf…" and queried Pinecone for
// org candidates matching *that*, totally ignoring the uploaded content.
//
// This implementation:
//   1. Extracts skills from each resume body via the org taxonomy.
//   2. Builds a synthetic Candidate per upload (id = resume entry id).
//   3. Builds a synthetic Job from the context JD (or paraphrase).
//   4. Runs the 6-signal inference engine per (resume × JD) pair.
//   5. Skips Pinecone entirely — the uploads aren't indexed yet, and
//      computing cosine-similarity between embeddings adds an extra
//      edge call per resume that typically fails when the breaker is
//      open. The `semantic` dimension is set to a neutral 50; the
//      inference engine redistributes its weight across the other
//      signals, so strong skill+experience alignment still surfaces.

export interface BulkResumeInput {
  id: string;
  name: string;
  text: string;
}

export interface BulkResumeSearchOptions {
  /**
   * Optional context JD — the body the user pasted in the "Role / JD
   * context" field of the bulk upload panel. If absent we fall back to
   * the paraphrase so scoring still has *some* target signal.
   */
  contextJd?: string | null;
  /** Free-form paraphrase used as a fallback JD when `contextJd` is missing. */
  paraphrase?: string;
  /** Display-only title override for the synthesised JD. */
  jdTitle?: string;
  /** Display-only client override for the synthesised JD. */
  clientName?: string;
}

export interface BulkResumeSearchResult {
  matches: MatchResult[];
  jd: { id: string; title: string; clientName: string };
  source: 'bulk-local' | 'empty';
  debug: {
    resumeCount: number;
    scoredCount: number;
    skippedCount: number;
    skillContextLoaded: boolean;
  };
}

/**
 * Heuristic "years of experience" extractor — scans the resume body for
 * the first "X years" / "X+ yrs" phrase and clamps to a sensible range.
 * Used as a fallback when the upload isn't a persisted candidate with a
 * `total_experience_years` column to read from.
 */
function inferExperienceYearsFromText(text: string): number | null {
  const m =
    text.match(/(\d{1,2})\s*\+?\s*(?:years|yrs|year)\b/i) ??
    text.match(/(?:experience|exp)\s*[:\-]?\s*(\d{1,2})\s*\+?\s*(?:years|yrs)?/i);
  if (!m) return null;
  const n = Number(m[1]);
  if (!Number.isFinite(n) || n < 0 || n > 50) return null;
  return n;
}

/**
 * Heuristic full-name extractor — the first non-empty line up to 6
 * words, stripped of common separators. Fallback is the file name.
 * This is display-only; downstream inference never touches `full_name`.
 */
function inferNameFromResume(text: string, fileName: string): string {
  const first = text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .find((l) => l.length > 0 && l.length < 120);
  if (!first) return fileName.replace(/\.[^.]+$/, '');
  const words = first.split(/\s+/).slice(0, 6).join(' ');
  if (/^[A-Za-z][A-Za-z\s.\-']{2,}$/.test(words)) return words;
  return fileName.replace(/\.[^.]+$/, '');
}

function buildSyntheticCandidate(
  orgId: string,
  resume: BulkResumeInput,
  skills: string[],
): Candidate {
  const years = inferExperienceYearsFromText(resume.text);
  const now = new Date().toISOString();
  return {
    id: resume.id,
    org_id: orgId,
    created_by: '',
    full_name: inferNameFromResume(resume.text, resume.name),
    email: '',
    phone: null,
    linkedin_url: null,
    source_channel: 'upload',
    source: null,
    utm_source: null,
    utm_medium: null,
    utm_campaign: null,
    current_company: null,
    current_title: null,
    location: null,
    base_resume_url: null,
    recruiter_rating: null,
    parsed_skills: skills,
    years_exp: years,
    total_experience_years: years,
    education: null,
    tags: [],
    notes: null,
    is_active: true,
    created_at: now,
    updated_at: now,
  } as unknown as Candidate;
}

/**
 * Score uploaded resumes against a context JD (or a paraphrase-derived
 * synthetic JD). Never throws — on failure returns an empty result set
 * with `source: 'empty'` so callers can surface a toast without crashing.
 */
export async function runBulkResumeSearch(
  orgId: string,
  resumes: BulkResumeInput[],
  opts: BulkResumeSearchOptions = {},
): Promise<BulkResumeSearchResult> {
  const debug = {
    resumeCount: resumes.length,
    scoredCount: 0,
    skippedCount: 0,
    skillContextLoaded: false,
  };
  const jdBody =
    (opts.contextJd && opts.contextJd.trim()) ||
    (opts.paraphrase && opts.paraphrase.trim()) ||
    '';

  if (!orgId || resumes.length === 0) {
    return {
      matches: [],
      jd: {
        id: '__bulk__',
        title: opts.jdTitle ?? 'Bulk resumes',
        clientName: opts.clientName ?? 'Client TBD',
      },
      source: 'empty',
      debug,
    };
  }

  const { canonicalNames: taxonomyNames, synonymMap } =
    await loadOrgSkillContext(orgId);
  debug.skillContextLoaded = taxonomyNames.length > 0;

  const jdSkills = jdBody
    ? extractSkillsFromPrompt(jdBody, taxonomyNames)
    : [];
  const resolvedTitle =
    opts.jdTitle?.trim() ||
    inferTitleFromKeywords(jdBody, jdSkills) ||
    (jdBody ? jdBody.slice(0, 80) : 'Bulk resume screen');
  const resolvedClient =
    opts.clientName?.trim() || defaultClientNameForJd(resolvedTitle);
  const syntheticJob = syntheticJobFromPrompt(orgId, jdBody || resolvedTitle, jdSkills, {
    id: '__bulk__',
    title: resolvedTitle,
    clientId: null,
    clientName: resolvedClient,
  });

  const matches: MatchResult[] = [];
  for (const r of resumes) {
    const body = (r.text ?? '').trim();
    if (!body) {
      debug.skippedCount += 1;
      continue;
    }
    try {
      const skills = extractSkillsFromPrompt(body, taxonomyNames);
      const candidate = buildSyntheticCandidate(orgId, r, skills);

      const skillResult = computeSkillOverlap(
        candidate,
        syntheticJob,
        synonymMap,
      );
      // Semantic left at neutral 50; the inference engine then leans on
      // skill overlap + experience alignment, which is what recruiters
      // actually care about for raw resume screens.
      const semantic = 50;
      const exp = experienceScore(
        candidate.total_experience_years ?? candidate.years_exp,
        null,
        null,
      );
      const budget = budgetScore(candidate.expected_ctc, null, null);
      const loc = locationScore(
        candidate.location,
        (candidate as { preferred_locations?: string[] | null })
          .preferred_locations ?? null,
        null,
        null,
      );
      const deadline = deadlineScore(
        (candidate as { notice_period_days?: number | null })
          .notice_period_days ?? null,
        null,
      );

      const inference = runInference(
        skillResult,
        semantic,
        exp,
        budget,
        loc,
        deadline,
      );
      matches.push(toMatchResult(orgId, candidate, inference, syntheticJob));
    } catch (err) {
      // Per-resume failure shouldn't abort the batch — log and move on
      // so the rest of the uploads still score.
      console.warn(
        '[runBulkResumeSearch] scoring failed for resume',
        r.name,
        err instanceof Error ? err.message : err,
      );
      debug.skippedCount += 1;
    }
  }

  matches.sort((a, b) => b.composite_score - a.composite_score);
  debug.scoredCount = matches.length;
  if (import.meta.env.DEV) {
    console.info('[runBulkResumeSearch] done', {
      resumeCount: debug.resumeCount,
      scoredCount: debug.scoredCount,
      skippedCount: debug.skippedCount,
      jdSkillCount: jdSkills.length,
      resolvedTitle,
    });
  }

  return {
    matches,
    jd: { id: '__bulk__', title: resolvedTitle, clientName: resolvedClient },
    source: matches.length > 0 ? 'bulk-local' : 'empty',
    debug,
  };
}

// ── Case B: Uploaded resumes × real org jobs (21-Apr-26) ────────────────────
//
// User feedback:
//   "if user give Resume/Resumes and no JD then Smart Match should show
//    against what all Jobs and clients present in the db it matches"
//
// Previously the bulk flow scored uploads against a single synthetic "Bulk
// resumes" JD, which is why every card rendered `Client TBD` and the group-
// by-client / by-job tabs collapsed to a single bucket. This function
// replaces that behaviour when no context JD was provided: we fetch the
// active jobs in the org and score every (resume, job) pair so the canvas
// can answer "which existing role does this resume fit?" — including the
// real client name, so grouping by client/job actually segments.
//
// Design notes:
//   - We cap the job pool at `maxJobs` (default 10) to keep the scoring
//     budget bounded. Most bulk screens happen in a narrow team scope so
//     10 is plenty; larger orgs can pass a higher cap.
//   - Only `open` and `on_hold` jobs are considered — scoring a resume
//     against a `filled` / `closed` role is noise.
//   - Each resume produces at most one MatchResult *per job*; we keep the
//     top-k (default 3) per resume so the canvas doesn't explode into
//     `resumes × jobs` cards — just the best fits for each upload.

export interface ResumeVsOrgJobsSearchOptions {
  /** Max jobs to consider from the org's active pool. Default 10. */
  maxJobs?: number;
  /** Keep only the top-K job matches per resume. Default 3. */
  perResumeTopK?: number;
}

export interface ResumeVsOrgJobsSearchResult {
  matches: MatchResult[];
  /**
   * The jobs that actually scored — feeds the group-by / filter UI so
   * the canvas can render the real client/job chips instead of a
   * "Client TBD" synthetic row.
   */
  jobs: Array<{
    id: string;
    title: string;
    clientId: string | null;
    clientName: string;
  }>;
  source: 'org-jobs' | 'empty';
  debug: {
    resumeCount: number;
    jobCount: number;
    pairScored: number;
    pairSkipped: number;
  };
}

/**
 * Score every uploaded resume against every active job in the org.
 *
 * Returns a flat `MatchResult[]` where each row is a (resume, job) pair
 * tagged with real `job_id` / `client_id`, so the canvas can group by
 * client / JD and surface "Also a fit for" correctly.
 */
export async function runResumeVsOrgJobsSearch(
  orgId: string,
  resumes: BulkResumeInput[],
  opts: ResumeVsOrgJobsSearchOptions = {},
): Promise<ResumeVsOrgJobsSearchResult> {
  const maxJobs = opts.maxJobs ?? 10;
  const perResumeTopK = opts.perResumeTopK ?? 3;
  const debug = {
    resumeCount: resumes.length,
    jobCount: 0,
    pairScored: 0,
    pairSkipped: 0,
  };
  if (!orgId || resumes.length === 0) {
    return { matches: [], jobs: [], source: 'empty', debug };
  }

  // 1. Load the active job pool. We select only the columns the inference
  //    engine + card need so we don't thrash a wide row for every pair.
  let jobRows: Array<
    Job & { client: { id: string; name: string } | null }
  > = [];
  try {
    const { data, error } = await supabase
      .from('ggj_b2b_jobs')
      .select(
        'id, org_id, client_id, title, description, skills_required, skills_nice_to_have, experience_min, experience_max, budget_min, budget_max, budget_currency, hiring_deadline, location, remote_policy, employment_type, status, created_at, updated_at, client:ggj_b2b_clients(id, name)',
      )
      .eq('org_id', orgId)
      .in('status', ['open', 'on_hold'])
      .order('created_at', { ascending: false })
      .limit(maxJobs);
    if (error) throw new Error(error.message);
    jobRows = (data ?? []) as unknown as Array<
      Job & { client: { id: string; name: string } | null }
    >;
  } catch (err) {
    console.warn(
      '[runResumeVsOrgJobsSearch] job pool fetch failed:',
      err instanceof Error ? err.message : err,
    );
    return { matches: [], jobs: [], source: 'empty', debug };
  }
  debug.jobCount = jobRows.length;
  if (jobRows.length === 0) {
    if (import.meta.env.DEV) {
      console.info(
        '[runResumeVsOrgJobsSearch] org has no active jobs — ' +
          'falling back to synthetic bulk JD via runBulkResumeSearch',
      );
    }
    return { matches: [], jobs: [], source: 'empty', debug };
  }

  // 2. Load the skill taxonomy once so every (resume, job) pair shares
  //    the same alias map. This is the most expensive call; batching it
  //    outside the loop is critical for throughput.
  const { canonicalNames: taxonomyNames, synonymMap } =
    await loadOrgSkillContext(orgId);
  void taxonomyNames;

  // 3. Pre-build synthetic candidates once per resume so we're not
  //    re-parsing the body inside the job loop.
  const cands = resumes
    .map((r) => {
      const body = (r.text ?? '').trim();
      if (!body) return null;
      const skills = extractSkillsFromPrompt(body, taxonomyNames);
      return {
        resume: r,
        body,
        candidate: buildSyntheticCandidate(orgId, r, skills),
      };
    })
    .filter((x): x is NonNullable<typeof x> => x !== null);

  // 4. Score every pair; keep top-K per resume so the canvas doesn't
  //    blow up into resumes × jobs cards.
  const perResume = new Map<string, MatchResult[]>();
  for (const { resume, candidate } of cands) {
    const rows: MatchResult[] = [];
    for (const job of jobRows) {
      try {
        const skillResult = computeSkillOverlap(candidate, job, synonymMap);
        // No per-pair Pinecone embedding — semantic stays neutral. If
        // we want real semantic similarity later, embed the JD +
        // resume once each and do a JS-side cosine; for the
        // first-pass UX this is accurate enough.
        const semantic = 50;
        const exp = experienceScore(
          candidate.total_experience_years ?? candidate.years_exp,
          (job as { experience_min?: number | null }).experience_min ?? null,
          (job as { experience_max?: number | null }).experience_max ?? null,
        );
        const budget = budgetScore(
          candidate.expected_ctc,
          (job as { budget_min?: number | null }).budget_min ?? null,
          (job as { budget_max?: number | null }).budget_max ?? null,
        );
        const loc = locationScore(
          candidate.location,
          (candidate as { preferred_locations?: string[] | null })
            .preferred_locations ?? null,
          (job as { location?: string | null }).location ?? null,
          (job as { remote_policy?: string | null }).remote_policy ?? null,
        );
        const deadline = deadlineScore(
          (candidate as { notice_period_days?: number | null })
            .notice_period_days ?? null,
          (job as { hiring_deadline?: string | null }).hiring_deadline ?? null,
        );
        const inference = runInference(
          skillResult,
          semantic,
          exp,
          budget,
          loc,
          deadline,
        );
        const jobWithClient = {
          ...job,
          client: job.client ?? null,
        } as unknown as Job;
        rows.push(toMatchResult(orgId, candidate, inference, jobWithClient));
        debug.pairScored += 1;
      } catch (err) {
        console.warn(
          '[runResumeVsOrgJobsSearch] pair scoring failed',
          resume.name,
          job.id,
          err instanceof Error ? err.message : err,
        );
        debug.pairSkipped += 1;
      }
    }
    rows.sort((a, b) => b.composite_score - a.composite_score);
    perResume.set(resume.id, rows.slice(0, perResumeTopK));
  }

  // 5. Flatten: each resume contributes its top-K job matches to the
  //    canvas. Ranking (1-based) is then assigned by the canvas over
  //    the full list sorted by composite_score.
  const allMatches: MatchResult[] = [];
  for (const rows of perResume.values()) allMatches.push(...rows);
  allMatches.sort((a, b) => b.composite_score - a.composite_score);

  // 6. Build the job-chip metadata so the canvas can render real
  //    client/job names in the group-by controls.
  const jobMeta = jobRows.map((j) => ({
    id: j.id,
    title: j.title,
    clientId: j.client_id ?? j.client?.id ?? null,
    clientName: j.client?.name ?? 'Untagged',
  }));

  if (import.meta.env.DEV) {
    console.info('[runResumeVsOrgJobsSearch] done', {
      resumeCount: debug.resumeCount,
      jobCount: debug.jobCount,
      pairScored: debug.pairScored,
      pairSkipped: debug.pairSkipped,
      totalReturned: allMatches.length,
    });
  }

  return {
    matches: allMatches,
    jobs: jobMeta,
    source: allMatches.length > 0 ? 'org-jobs' : 'empty',
    debug,
  };
}
